import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EditIcon from '@mui/icons-material/Edit';
import type { CompatibilityVerdict, ProjectRecipeSummary } from '../../services/types';

interface CompatibilityDialogProps {
  open: boolean;
  verdict: CompatibilityVerdict | null;
  recipe: ProjectRecipeSummary | null;
  batchId: string;
  onApplyAndLoad: () => void;
  onReviewFirst: () => void;
  onRegenerateDelta: () => void;
  onRegenerateFull: () => void;
  onCancel?: () => void;
  /** When true, all action buttons are disabled (async operation in flight). */
  busy?: boolean;
  /** Whether this comparison is against the project's own recipe or a cross-project one */
  baselineSource?: 'own' | 'cross_project' | null;
  /** Name of the source project when cross-project baselining */
  sourceProjectName?: string;
  /** Opens RecipePickerDialog to select a different baseline */
  onChangeBaseline?: () => void;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/* ------------------------------------------------------------------ */
/*  Shared: Schema diff findings summary                               */
/* ------------------------------------------------------------------ */

interface ChangeLineProps {
  icon: React.ReactNode;
  color: string;
  text: string;
}

function ChangeLine({ icon, color, text }: ChangeLineProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
      <Box sx={{ color, display: 'flex', alignItems: 'center', fontSize: 16 }}>{icon}</Box>
      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{text}</Typography>
    </Box>
  );
}

function SchemaFindings({ verdict }: { verdict: CompatibilityVerdict }) {
  const srcDiff = verdict.source_diff || {};
  const tgtDiff = verdict.target_diff || {};

  const lines: React.ReactNode[] = [];

  // Source file-level changes
  for (const f of (srcDiff.files_added || []).slice(0, 5)) {
    lines.push(
      <ChangeLine key={`sa-${f}`} icon={<AddIcon fontSize="inherit" />} color="success.main" text={`Source file added: ${f}`} />,
    );
  }
  for (const f of (srcDiff.files_removed || []).slice(0, 5)) {
    lines.push(
      <ChangeLine key={`sr-${f}`} icon={<RemoveIcon fontSize="inherit" />} color="error.main" text={`Source file removed: ${f}`} />,
    );
  }
  for (const r of (srcDiff.file_renames || []).slice(0, 5)) {
    lines.push(
      <ChangeLine key={`sren-${r.old_alias}`} icon={<SwapHorizIcon fontSize="inherit" />} color="info.main" text={`Source renamed: ${r.old_alias} → ${r.new_alias}`} />,
    );
  }

  // Source column-level changes
  for (const fc of (srcDiff.files_changed || []).slice(0, 5)) {
    const alias = fc.new_alias || fc.old_alias || '?';
    for (const col of (fc.columns_added || []).slice(0, 3)) {
      lines.push(
        <ChangeLine key={`sca-${alias}-${col}`} icon={<AddIcon fontSize="inherit" />} color="success.main" text={`${alias}: +${col}`} />,
      );
    }
    for (const col of (fc.columns_removed || []).slice(0, 3)) {
      lines.push(
        <ChangeLine key={`scr-${alias}-${col}`} icon={<RemoveIcon fontSize="inherit" />} color="error.main" text={`${alias}: -${col}`} />,
      );
    }
    for (const tc of (fc.column_type_changes || []).slice(0, 3)) {
      lines.push(
        <ChangeLine key={`sct-${alias}-${tc.column}`} icon={<EditIcon fontSize="inherit" />} color="warning.main" text={`${alias}.${tc.column}: ${tc.old_type} → ${tc.new_type}`} />,
      );
    }
  }

  // Target table-level changes
  for (const t of (tgtDiff.tables_added || []).slice(0, 3)) {
    lines.push(
      <ChangeLine key={`ta-${t}`} icon={<AddIcon fontSize="inherit" />} color="success.main" text={`Target table added: ${t}`} />,
    );
  }
  for (const t of (tgtDiff.tables_removed || []).slice(0, 3)) {
    lines.push(
      <ChangeLine key={`tr-${t}`} icon={<RemoveIcon fontSize="inherit" />} color="error.main" text={`Target table removed: ${t}`} />,
    );
  }

  // Target column-level changes
  for (const tc of (tgtDiff.tables_changed || []).slice(0, 5)) {
    const table = tc.table || '?';
    for (const col of (tc.columns_added || []).slice(0, 3)) {
      lines.push(
        <ChangeLine key={`tca-${table}-${col}`} icon={<AddIcon fontSize="inherit" />} color="success.main" text={`${table}: +${col}`} />,
      );
    }
    for (const col of (tc.columns_removed || []).slice(0, 3)) {
      lines.push(
        <ChangeLine key={`tcr-${table}-${col}`} icon={<RemoveIcon fontSize="inherit" />} color="error.main" text={`${table}: -${col}`} />,
      );
    }
    for (const ctc of (tc.column_type_changes || []).slice(0, 3)) {
      lines.push(
        <ChangeLine key={`tct-${table}-${ctc.column}`} icon={<EditIcon fontSize="inherit" />} color="warning.main" text={`${table}.${ctc.column}: ${ctc.left_type} → ${ctc.right_type}`} />,
      );
    }
  }

  if (lines.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        What Changed
      </Typography>
      <Box
        sx={{
          background: '#f8f9fa',
          borderRadius: 1.5,
          p: 1.5,
          maxHeight: 200,
          overflowY: 'auto',
        }}
      >
        {lines}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared: Stats panel                                                */
/* ------------------------------------------------------------------ */

function DeltaStatsPanel({ verdict, recipe }: { verdict: CompatibilityVerdict; recipe?: ProjectRecipeSummary | null }) {
  const srcSummary = verdict.source_diff?.summary || {};
  const tgtSummary = verdict.target_diff?.summary || {};

  const newColumns =
    (srcSummary.files_added || 0) +
    (tgtSummary.columns_added || 0) +
    (tgtSummary.tables_added || 0);
  const broken =
    (srcSummary.files_removed || 0) +
    (tgtSummary.columns_removed || 0) +
    (tgtSummary.tables_removed || 0);
  const stale =
    (srcSummary.file_renames || 0) +
    (tgtSummary.column_type_changes || 0);

  const totalMappings = recipe?.mapping_count ?? 0;
  const inherited = Math.max(0, totalMappings - newColumns - broken - stale);

  if (newColumns === 0 && broken === 0 && stale === 0 && inherited === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        textAlign: 'center',
        background: '#f8f9fa',
        borderRadius: 2,
        p: 1.5,
        mb: 2,
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" color="info.main">{inherited}</Typography>
        <Typography variant="caption" color="text.secondary">Inherited</Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" color="success.main">{newColumns}</Typography>
        <Typography variant="caption" color="text.secondary">New (AI)</Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" color="error.main">{broken}</Typography>
        <Typography variant="caption" color="text.secondary">Broken</Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h6" color="warning.main">{stale}</Typography>
        <Typography variant="caption" color="text.secondary">Stale</Typography>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Mode-specific content                                              */
/* ------------------------------------------------------------------ */

function DirectContent({
  recipe,
  verdict,
  onApplyAndLoad,
  onReviewFirst,
  onRegenerateFull,
  onCancel,
  busy,
}: {
  recipe: ProjectRecipeSummary | null;
  verdict: CompatibilityVerdict;
  onApplyAndLoad: () => void;
  onReviewFirst: () => void;
  onRegenerateFull: () => void;
  onCancel?: () => void;
  busy?: boolean;
}) {
  return (
    <>
      <Alert severity="success" sx={{ mb: 2 }}>
        Compatible — the existing recipe can be applied directly to this batch.
      </Alert>

      {recipe && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Recipe Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Chip
              label={`${recipe.mapping_count ?? 0} mappings`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`Approved ${formatDate(recipe.approved_at)}`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${recipe.execution_count ?? 0} executions`}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
      )}

      <SchemaFindings verdict={verdict} />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        {onCancel && (
          <Button variant="text" color="inherit" onClick={onCancel} disabled={busy}>
            Cancel Upload
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button variant="text" onClick={onRegenerateFull} disabled={busy}>
            Start Fresh
          </Button>
          <Button variant="outlined" onClick={onReviewFirst} disabled={busy}>
            Review First
          </Button>
          <Button variant="contained" onClick={onApplyAndLoad} disabled={busy}>
            Apply &amp; Load
            <Chip label="Recommended" size="small" color="success" sx={{ ml: 1, height: 20 }} />
          </Button>
        </Box>
      </Box>
    </>
  );
}

function DeltaContent({
  verdict,
  recipe,
  onRegenerateDelta,
  onRegenerateFull,
  onCancel,
  busy,
}: {
  verdict: CompatibilityVerdict;
  recipe?: ProjectRecipeSummary | null;
  onRegenerateDelta: () => void;
  onRegenerateFull: () => void;
  onCancel?: () => void;
  busy?: boolean;
}) {
  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Schema Changes Detected — the source or target schema has changed since the recipe was
        approved.
      </Alert>

      <SchemaFindings verdict={verdict} />

      <DeltaStatsPanel verdict={verdict} recipe={recipe} />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        {onCancel && (
          <Button variant="text" color="inherit" onClick={onCancel} disabled={busy}>
            Cancel Upload
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button variant="outlined" onClick={onRegenerateFull} disabled={busy}>
            Start Fresh
          </Button>
          <Button variant="contained" color="warning" onClick={onRegenerateDelta} disabled={busy}>
            Regenerate (Delta)
            <Chip label="Recommended" size="small" color="warning" sx={{ ml: 1, height: 20 }} />
          </Button>
        </Box>
      </Box>
    </>
  );
}

function FullRequiredContent({
  verdict,
  onRegenerateFull,
  onCancel,
  busy,
}: {
  verdict: CompatibilityVerdict;
  onRegenerateFull: () => void;
  onCancel?: () => void;
  busy?: boolean;
}) {
  return (
    <>
      <Alert severity="error" sx={{ mb: 2 }}>
        Incompatible Schema — the schema changes are too significant to patch incrementally.
      </Alert>

      {verdict.reason && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">{verdict.reason}</Typography>
        </Box>
      )}

      <SchemaFindings verdict={verdict} />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
        {onCancel && (
          <Button variant="text" color="inherit" onClick={onCancel} disabled={busy}>
            Cancel Upload
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Button variant="contained" color="error" onClick={onRegenerateFull} disabled={busy}>
            Start Fresh
          </Button>
        </Box>
      </Box>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Main dialog                                                        */
/* ------------------------------------------------------------------ */

function CompatibilityDialog({
  open,
  verdict,
  recipe,
  batchId,
  onApplyAndLoad,
  onReviewFirst,
  onRegenerateDelta,
  onRegenerateFull,
  onCancel,
  busy,
  baselineSource,
  sourceProjectName,
  onChangeBaseline,
}: CompatibilityDialogProps) {
  if (!verdict) return null;

  const titleByMode: Record<CompatibilityVerdict['mode'], string> = {
    direct: 'Recipe Compatibility',
    delta: 'Schema Changes Detected',
    full_required: 'Incompatible Schema',
  };

  return (
    <Dialog open={open} onClose={() => {}} maxWidth={false} PaperProps={{ sx: { maxWidth: 540 } }} disableEscapeKeyDown>
      <DialogTitle>{titleByMode[verdict.mode]}</DialogTitle>
      <DialogContent>
        {/* Cross-project baseline indicator */}
        {baselineSource === 'cross_project' && (
          <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
            Comparing against recipe from project{' '}
            <strong>{sourceProjectName || recipe?.project_name || 'another project'}</strong>
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Batch <strong>{batchId}</strong>
          {recipe ? (
            <>
              {' '}
              — Recipe <strong>{recipe.recipe_id}</strong>
            </>
          ) : null}
        </Typography>

        {verdict.mode === 'direct' && (
          <DirectContent
            recipe={recipe}
            verdict={verdict}
            onApplyAndLoad={onApplyAndLoad}
            onReviewFirst={onReviewFirst}
            onRegenerateFull={onRegenerateFull}
            onCancel={onCancel}
            busy={busy}
          />
        )}

        {verdict.mode === 'delta' && (
          <DeltaContent
            verdict={verdict}
            recipe={recipe}
            onRegenerateDelta={onRegenerateDelta}
            onRegenerateFull={onRegenerateFull}
            onCancel={onCancel}
            busy={busy}
          />
        )}

        {verdict.mode === 'full_required' && (
          <FullRequiredContent
            verdict={verdict}
            onRegenerateFull={onRegenerateFull}
            onCancel={onCancel}
            busy={busy}
          />
        )}

        {/* "Use Different Baseline" link — always available */}
        {onChangeBaseline && (
          <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Button
              size="small"
              variant="text"
              onClick={onChangeBaseline}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}
            >
              Use Different Baseline
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CompatibilityDialog;
